"use client";
import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import type { Store, StoreTag, Region } from "@/lib/types";
import { logoChainForStore } from "@/lib/logo";
import { cachedSrc } from "@/lib/img";
import { applyRegionCurrency } from "@/lib/currency";
import { useEditContext } from "@/lib/edit-context";
import InfoModal from "./InfoModal";
import CryptoInstructionsGate from "./CryptoInstructionsGate";
import { useEntranceReady } from "@/lib/loading-screen-gate";
import { getTelegraphContent, getStoreInfoByDomain, type TelegraphContent } from "@/data/telegraph-content";

function parseNotes(text: string, onOpen: (content: TelegraphContent) => void) {
  const re = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts: (string | JSX.Element)[] = [];
  let last = 0, ki = 0, m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const label = m[1];
    const url = m[2];
    // When a "click to view" link points at a source page we mirror, open it
    // IN-PLACE (recreated content + close button) instead of redirecting away.
    // A <button> is used so the root layout's admin edit-mode anchor click
    // guard never swallows the trigger.
    const linked = getTelegraphContent(url);
    if (linked) {
      parts.push(
        <button
          key={ki++}
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpen(linked); }}
          className="inline cursor-pointer border-0 bg-transparent p-0 align-baseline font-bold text-amber-300 underline underline-offset-2 hover:text-amber-200"
        >
          {label}
        </button>,
      );
    } else {
      parts.push(
        <a
          key={ki++}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-2 text-amber-300 hover:text-amber-200 break-all"
          onClick={(e) => e.stopPropagation()}
        >
          {label}
        </a>,
      );
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

// Featured tags drive the card's animated glow border. Highest priority
// wins when a store carries several. This replaced the old manual
// prismaticGlow checkbox — the tag now auto-sets the glow.
const GLOW_BY_TAG: Partial<Record<StoreTag, string>> = {
  crown: "glow-luxury",
  diamond: "glow-premium",
  fire: "glow-hot",
  new: "glow-new",
};
const GLOW_PRIORITY: StoreTag[] = ["crown", "diamond", "fire", "new"];

const TAG_LABEL: Record<string, { label: string; cls: string }> = {
  fire:    { label: "🔥 hot",       cls: "bg-orange-500/15 text-orange-300 ring-orange-400/30" },
  diamond: { label: "💎 premium",   cls: "bg-sky-400/15 text-sky-200 ring-sky-300/30" },
  crown:   { label: "👑 luxury",    cls: "bg-amber-400/15 text-amber-200 ring-amber-300/30" },
  global:  { label: "🌎 worldwide", cls: "bg-emerald-400/15 text-emerald-200 ring-emerald-300/30" },
  new:     { label: "✨ new",       cls: "bg-fuchsia-400/15 text-fuchsia-200 ring-fuchsia-300/30" },
};

type StoreCardProps = {
  store: Store;
  /** Active region tab the card is shown under. Drives the price-limit
   *  currency symbol (UK → £, EU → €, USA/CAD → $) so a store that spans
   *  several regions shows the right currency in each region's list. */
  region?: Region;
  idx: number;
  /** Admin display-label overrides (category key → label). When a category
   *  has a renamed label it is shown here so the card matches the section
   *  header and the editor; the underlying key on the store is unchanged. */
  categoryLabels?: Record<string, string>;
  /** Inline-editor callbacks. When omitted the card behaves normally. */
  onEdit?: (store: Store) => void;
  onDelete?: (store: Store) => void;
  /** Drag handlers wired by StoreFilters when in edit mode. */
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent<HTMLElement>) => void;
  onDragOver?: (e: React.DragEvent<HTMLElement>) => void;
  onDrop?: (e: React.DragEvent<HTMLElement>) => void;
  onDragEnd?: (e: React.DragEvent<HTMLElement>) => void;
  /** When set, this card is a shareable deep-link target: it renders
   *  `id={anchorId}` + scroll-mt and auto-opens its info popup when the page
   *  loads with `#<anchorId>`. (The visible "copy link" pill was removed.) */
  anchorId?: string;
};

export default function StoreCard({
  store,
  region,
  idx,
  categoryLabels,
  anchorId,
  onEdit,
  onDelete,
  draggable,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: StoreCardProps) {
  // logoChainForStore consults the name-based override map first, so stores
  // with a wrong or missing domain still resolve to the right logo.
  // Chain order: Google S2 → DDG → Clearbit (fewest browser 404s first).
  const overrideChain = logoChainForStore(store.name, store.domain);
  const fallbacks = store.logoUrl
    ? [store.logoUrl, ...overrideChain.filter((u) => u !== store.logoUrl)]
    : overrideChain;
  const [logoIdx, setLogoIdx] = useState(0);
  const logoSrc = fallbacks[logoIdx];

  // In-place info panel: cards open recreated content here instead of
  // navigating away. Triggered either by a mirrored note link (parseNotes) or,
  // for stores with no inline link, by domain match (e.g. StubHub).
  const [info, setInfo] = useState<TelegraphContent | null>(null);
  const domainInfo = getStoreInfoByDomain(store.domain);

  // Gate the deep-link auto-open on the same boot signal the scroller uses.
  const entranceReady = useEntranceReady();

  // The first note link that resolves to recreated content. Kept separate
  // from the domain fallback so the "CLICK TO READ FULL INFO" button below
  // can be suppressed when the notes already carry a working inline trigger
  // (otherwise PayPal cards would show TWO buttons opening the same popup).
  const inlineInfo = useMemo<TelegraphContent | null>(() => {
    if (!store.notes) return null;
    const re = /\[([^\]]+)\]\(([^)]+)\)/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(store.notes))) {
      const linked = getTelegraphContent(m[2]);
      if (linked) return linked;
    }
    return null;
  }, [store.notes]);

  // The popup this card opens when deep-linked: the first mirrored note link
  // that resolves to recreated content, else a domain match (e.g. StubHub).
  const primaryInfo = inlineInfo ?? domainInfo ?? null;

  // Deep link: on a fresh load of /store-list#<anchorId> auto-open this card's
  // info popup. Opening is DELAYED so StoreListHashScroll's instant-scroll
  // lands FIRST — InfoModal sets `document.body.style.overflow = "hidden"` on
  // open, which would otherwise freeze the page before it reached the card.
  useEffect(() => {
    if (!anchorId || typeof window === "undefined" || !primaryInfo) return;
    // Wait until any boot splash has cleared (mirrors StoreListHashScroll) so
    // the deep-link scroll lands BEFORE the modal locks body scroll. With no
    // splash present `entranceReady` is true on mount, so this runs right away.
    if (!entranceReady) return;
    let frag = "";
    try {
      frag = decodeURIComponent(window.location.hash.slice(1));
    } catch {
      frag = window.location.hash.slice(1);
    }
    if (frag !== anchorId) return;
    const t = window.setTimeout(() => setInfo(primaryInfo), 600);
    return () => window.clearTimeout(t);
  }, [anchorId, primaryInfo, entranceReady]);

  const initial = store.name.replace(/[^a-zA-Z]/g, "")[0]?.toUpperCase() || "?";

  // Pick the glow border for the highest-priority featured tag (if any).
  const glowTag = GLOW_PRIORITY.find((t) => (store.tags ?? []).includes(t));
  const glowClass = glowTag ? GLOW_BY_TAG[glowTag] : null;

  // Editor overlay only shows when admin is in edit mode AND a parent
  // (StoreFilters) supplied callbacks — keeps this component usable on
  // public pages with zero visual change for non-admin visitors.
  const { isAdmin, editMode } = useEditContext();
  const showOverlay = isAdmin && editMode && (onEdit || onDelete);

  // NOTE: HTML5 drag-and-drop handlers are intentionally placed on the
  // inner plain <div> rather than the outer <motion.article>. framer-motion
  // overloads the `onDragStart` / `onDragEnd` props on its `motion.*`
  // components with its own pan-gesture signature `(e, info: PanInfo)`,
  // which is type-incompatible with React's `DragEvent<HTMLElement>`.
  // Putting the HTML drag handlers on the inner div sidesteps the conflict
  // entirely and keeps both layout animation AND native drag working.
  // v6.7 — REMOVED the MeshEntrance wrapper around store-info cards.
  // Per the design brief: store cards on the store-list page should
  // render statically with no entrance animation; only the "rules"
  // boxcards (handled separately on app/store-list/page.tsx) get an
  // entrance. The mesh warp on every store card was reading as a
  // disorienting "all the data is mid-warping into focus" on initial
  // page load with hundreds of cards.
  return (
    <motion.article
      suppressHydrationWarning
      id={anchorId}
      data-cursor="hover"
      data-cursor-label={store.name}
      data-testid={`store-card-${store.id}`}
      data-editable-skip
      whileHover={{ y: -4 }}
      className={`group relative scroll-mt-24 ${glowClass ? "p-[1.5px]" : "p-px"} rounded-2xl ${
        glowClass ?? "bg-white/10"
      } transition-shadow duration-300 hover:shadow-[0_30px_70px_-25px_rgba(245,185,69,0.18)] ${
        showOverlay ? "ring-2 ring-amber-300/0 hover:ring-amber-300/40" : ""
      }`}
    >
      <div
        className="relative h-full rounded-2xl bg-ink-900/95 p-5 backdrop-blur-xl transition group-hover:bg-ink-800/95"
        draggable={draggable}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onDragEnd={onDragEnd}
      >
        {showOverlay && (
          <div className="absolute right-2 top-2 z-10 flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
            <span
              className="grid h-7 w-7 cursor-grab place-items-center rounded-md border border-white/20 bg-ink-900/90 text-white/70"
              title="Drag to reorder"
              aria-hidden
              data-testid={`store-card-drag-${store.id}`}
            >
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor">
                <circle cx="9" cy="6" r="1.5" /><circle cx="15" cy="6" r="1.5" />
                <circle cx="9" cy="12" r="1.5" /><circle cx="15" cy="12" r="1.5" />
                <circle cx="9" cy="18" r="1.5" /><circle cx="15" cy="18" r="1.5" />
              </svg>
            </span>
            {onEdit && (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEdit(store); }}
                className="grid h-7 w-7 place-items-center rounded-md border border-amber-300/40 bg-amber-400/15 text-amber-200 transition hover:bg-amber-400/30"
                title="Edit store"
                aria-label="Edit store"
                data-testid={`store-card-edit-${store.id}`}
              >
                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                </svg>
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (confirm(`Delete "${store.name}"? This can't be undone from the inline editor.`)) {
                    onDelete(store);
                  }
                }}
                className="grid h-7 w-7 place-items-center rounded-md border border-rose-300/40 bg-rose-500/15 text-rose-200 transition hover:bg-rose-500/30"
                title="Delete store"
                aria-label="Delete store"
                data-testid={`store-card-delete-${store.id}`}
              >
                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 6h18" /><path d="M19 6l-1.6 13.2A2 2 0 0 1 15.4 21H8.6a2 2 0 0 1-2-1.8L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                </svg>
              </button>
            )}
          </div>
        )}
        <div className="flex items-start gap-4">
          <div className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-xl border border-white/10 bg-white">
            {logoSrc ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={cachedSrc(logoSrc)}
                alt={`${store.name} logo`}
                width={56}
                height={56}
                loading="lazy"
                data-editable-skip=""
                className="h-full w-full object-cover"
                onError={() => setLogoIdx((i) => i + 1)}
              />
            ) : (
              <span className="text-xl font-bold text-ink-900">{initial}</span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="heading-display truncate text-lg font-semibold tracking-tight text-white">
                {store.name}
              </h3>
              {(store.regions ?? []).map((r) => (
                <span key={r} className="rounded-full bg-white/8 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-white/60 ring-1 ring-white/10">
                  {r}
                </span>
              ))}
            </div>
            <p className="text-xs text-white/65 font-medium">{(store.categories ?? []).map((c) => categoryLabels?.[c] ?? c).join(' · ')}</p>
            {(store.tags ?? []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(store.tags ?? []).map((t) => {
                  const meta = TAG_LABEL[t];
                  if (!meta) return null;
                  return (
                    <span
                      key={t}
                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${meta.cls}`}
                    >
                      {meta.label}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Metric tiles */}
        <div className="mt-4 grid grid-cols-2 gap-2">
          <Metric label="Limit" value={applyRegionCurrency(store.priceLimit, region ?? store.regions?.[0] ?? "USA") ?? "—"} accent="amber" />
          <Metric label="Items" value={store.itemLimit ?? "—"} accent="sky" />
          <Metric label="Fee" value={store.fee ?? "—"} accent="emerald" />
          <Metric label="Time" value={store.timeframe ?? "—"} accent="violet" />
        </div>

        {store.notes && (
          <p className="mt-3 whitespace-pre-wrap break-words text-sm font-bold leading-relaxed text-white/85">
            {parseNotes(store.notes, setInfo)}
          </p>
        )}

        {/* Stores with no inline link (e.g. StubHub) still get an in-place
            info popup, triggered by domain match. Suppressed when the notes
            already render a mirrored inline trigger (inlineInfo) so a card
            never shows two buttons opening the same popup. A <button> dodges
            the admin edit-mode anchor click guard, same as the note-link
            triggers. */}
        {!inlineInfo && domainInfo && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setInfo(domainInfo); }}
            className="mt-3 inline-flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-sm font-bold text-amber-300 underline underline-offset-2 hover:text-amber-200"
          >
            CLICK TO READ FULL INFO
          </button>
        )}

        {store.domain && (
          <a
            href={`https://${store.domain}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-amber-300/85 hover:text-amber-200"
          >
            {store.domain}
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M7 17 17 7M7 7h10v10" />
            </svg>
          </a>
        )}

        {info && (
          <InfoModal
            open
            onClose={() => setInfo(null)}
            title={store.name}
            html={info.html}
            contentId={info.id}
            afterContent={
              info.id === "crypto" ? <CryptoInstructionsGate /> : undefined
            }
          />
        )}
      </div>
    </motion.article>
  );
}

function Metric({ label, value, accent }: { label: string; value: string; accent: "amber" | "sky" | "emerald" | "violet" }) {
  const cls = {
    amber:   "from-amber-400/15 to-amber-600/5 text-amber-100",
    sky:     "from-sky-400/15 to-sky-600/5 text-sky-100",
    emerald: "from-emerald-400/15 to-emerald-600/5 text-emerald-100",
    violet:  "from-violet-400/15 to-violet-600/5 text-violet-100",
  }[accent];
  return (
    <div className={`rounded-lg bg-gradient-to-br ${cls} border border-white/5 px-2.5 py-1.5`}>
      <div className="text-[9px] font-semibold uppercase tracking-wider text-white/45">
        {label}
      </div>
      <div className="truncate text-sm font-semibold">{value}</div>
    </div>
  );
}
